import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ColFilter } from './columnFilter';

interface Props {
  title: string;
  anchor: DOMRect;
  distinctValues: string[];
  current?: ColFilter;
  sortDir: 'asc' | 'desc' | null;
  onSort: (dir: 'asc' | 'desc') => void;
  onApply: (f: ColFilter | null) => void;
  onClose: () => void;
}

const WIDTH = 280;

// Funnel dropdown: sort A→Z / Z→A, a live value search, and a checkbox
// multiselect ("in" filter) with Reset / Apply. Portaled + fixed so it escapes
// the table's overflow, positioned under the header and clamped to the viewport.
export function ColumnFilterPopover({ title, anchor, distinctValues, current, sortDir, onSort, onApply, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(current?.op === 'in' ? current.values : []),
  );
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: -9999, top: -9999 });

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const gap = 4;
    const h = panel?.offsetHeight ?? 360;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.left;
    if (left + WIDTH > vw - 8) left = vw - WIDTH - 8;
    if (left < 8) left = 8;
    let top = anchor.bottom + gap;
    if (top + h > vh - 8) top = Math.max(8, anchor.top - h - gap);
    setPos({ left, top });
  }, [anchor]);

  // Close on outside click / Escape.
  useLayoutEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const shown = useMemo(
    () => distinctValues.filter((v) => v.toLowerCase().includes(search.toLowerCase())),
    [distinctValues, search],
  );

  const toggle = (v: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  return createPortal(
    <div ref={panelRef} className="rd-col-filter-dropdown" style={{ left: pos.left, top: pos.top, width: WIDTH }}>
      <div className="rd-col-filter-title">Filter &amp; sort: {title}</div>
      <div className="d-flex gap-2 mb-2">
        <button
          type="button"
          className={`btn btn-sm flex-fill rd-col-sort-btn${sortDir === 'asc' ? ' on' : ''}`}
          onClick={() => onSort('asc')}
        >
          A → Z
        </button>
        <button
          type="button"
          className={`btn btn-sm flex-fill rd-col-sort-btn${sortDir === 'desc' ? ' on' : ''}`}
          onClick={() => onSort('desc')}
        >
          Z → A
        </button>
      </div>
      <input
        className="form-control form-control-sm mb-2"
        placeholder="Type to filter values…"
        autoComplete="off"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="rd-col-filter-values">
        {shown.length === 0 ? (
          <div className="text-muted fs-12 px-1 py-2">No values</div>
        ) : (
          shown.map((v) => (
            <label className="form-check rd-col-filter-check" key={v}>
              <input className="form-check-input" type="checkbox" checked={checked.has(v)} onChange={() => toggle(v)} />
              <span className="form-check-label">{v === '' ? '(empty)' : v}</span>
            </label>
          ))
        )}
      </div>
      <div className="d-flex gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-light flex-fill"
          onClick={() => {
            setChecked(new Set());
            setSearch('');
            onApply(null);
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary flex-fill"
          onClick={() => onApply(checked.size ? { op: 'in', values: [...checked] } : null)}
        >
          Apply
        </button>
      </div>
    </div>,
    document.body,
  );
}
